import React, { useState } from 'react';

import { Box } from '@vk-hr-tek/ui/Box';
import { Link } from '@vk-hr-tek/ui/Link';
import { Typography } from '@vk-hr-tek/ui/Typography';
import { WysiwygContent } from '@vk-hr-tek/ui/WysiwygContent';
import { ColoredChip } from '@vk-hr-tek/ui/ColoredChip';
import { Icon } from '@vk-hr-tek/ui/Icon';
import { Divider } from '@vk-hr-tek/ui/Divider';
import { Tooltip } from '@vk-hr-tek/ui/Tooltip';
import { useMeasureOverflow } from '@vk-hr-tek/ui/hooks';

import {
  AnswersTypes,
  SurveyAnalyticsQuestionIconNames,
} from './SurveyAnalyticsQuestion.types';

const parseEditorState = (data: string) => {
  try {
    return JSON.parse(data);
  } catch {
    return {};
  }
};

const questionDataByType = (
  answerType: AnswersTypes,
): { icon: SurveyAnalyticsQuestionIconNames; tooltipText: string } => {
  switch (answerType) {
    case 'emoji':
      return { icon: 'happy_face_outline', tooltipText: 'Оценка смайлами' };
    case 'free_text':
      return { icon: 'article_outline', tooltipText: 'Текст' };
    case 'multiple_choice':
      return {
        icon: 'check_square_outline',
        tooltipText: 'Несколько из списка',
      };
    case 'scale':
      return { icon: 'vkhrtek_slider_outline', tooltipText: 'Шкала' };
    case 'single_choice':
      return { icon: 'check_circle_outline', tooltipText: 'Один из списка' };
    case 'stars':
      return { icon: 'favorite_outline', tooltipText: 'Оценка звездами' };
  }
};

interface SurveyAnalyticsQuestionProps {
  questionSettings: {
    required: boolean;
    skipped: boolean;
  };
  content: {
    data?: string;
    attachments?: { id: string; type: 'image' | 'file' }[];
  };
  answerType: AnswersTypes;
  totalAnswers: number;
  getAttachmentUrl: (id: string) => Promise<string>;
  description?: string;
  flat?: boolean;
  children?: React.ReactNode;
}

const MAX_CONTENT_HEIGHT = 352;

export const SurveyAnalyticsQuestion = ({
  questionSettings,
  content,
  answerType,
  totalAnswers,
  getAttachmentUrl,
  description,
  flat = false,
  children,
}: SurveyAnalyticsQuestionProps) => {
  const { measureRef: questionRef, isOverflowing: isQuestionOverflowing } =
    useMeasureOverflow<HTMLDivElement>();
  const [isQuestionExpanded, setIsQuestionExpanded] = useState(false);

  const { icon, tooltipText } = questionDataByType(answerType);

  return (
    <Box
      bgcolor={flat ? 'bg.greyscale.primary' : 'bg.light.primary'}
      display="flex"
      flexDirection="column"
      gap="16"
      radius="l"
      border={flat ? '0' : '1px solid'}
      borderColor="stroke.primary"
    >
      <Box display="flex" flexDirection="column" gap="8" px={flat ? '24' : '0'}>
        <Box
          ref={questionRef}
          maxHeight={
            isQuestionOverflowing && isQuestionExpanded
              ? questionRef.current?.scrollHeight
              : MAX_CONTENT_HEIGHT
          }
          overflow="hidden"
          display="flex"
          flexDirection="column"
          gap="8"
          p={flat ? '0' : '16'}
          pb="0"
          {...(isQuestionOverflowing &&
            isQuestionExpanded && {
              maskImage: 'none',
              WebkitMaskImage: 'none',
            })}
          {...(isQuestionOverflowing &&
            !isQuestionExpanded && {
              maskImage:
                'linear-gradient(to bottom, black calc(100% - 32px), transparent calc(100% - 8px))',
              WebkitMaskImage:
                'linear-gradient(to bottom, black calc(100% - 32px), transparent calc(100% - 8px))',
            })}
          transition="max-height 0.5s"
        >
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Box display="flex" alignItems="center" gap="12">
              {questionSettings.required && (
                <ColoredChip
                  size="extraSmall"
                  color="blue"
                  label="Обязательный вопрос"
                />
              )}

              {questionSettings.skipped && (
                <ColoredChip
                  size="extraSmall"
                  color="orange"
                  label="Пропущен"
                  isBgWhite={flat}
                />
              )}

              <ColoredChip
                size="extraSmall"
                color="gray"
                isBgWhite={flat}
                label="Ответов"
                counter={totalAnswers}
              />
            </Box>

            <Tooltip placement="top" title={tooltipText}>
              <Box>
                <Icon name={icon} color="stroke.secondary" size="20" />
              </Box>
            </Tooltip>
          </Box>

          <WysiwygContent
            editorStateJSON={parseEditorState(content?.data || '{}')}
            getAttachmentUrl={getAttachmentUrl}
          />

          {description && (
            <Typography variant="body3" color="text.light.secondary">
              {description}
            </Typography>
          )}
        </Box>

        {isQuestionOverflowing && (
          <Box pl="16">
            <Link
              stroke={false}
              onClick={() => setIsQuestionExpanded((prev) => !prev)}
              variant="tertiary"
            >
              {isQuestionExpanded ? 'Скрыть' : 'Показать еще'}
            </Link>
          </Box>
        )}
      </Box>

      <Divider />

      <Box
        overflow="hidden"
        px={flat ? '24' : '16'}
        pb={flat ? '0' : '16'}
        pt="0"
      >
        {children || (
          <Box radius="m" bgcolor="bg.greyscale.secondary" px="12" py="8">
            <Typography variant="body3" color="text.light.tertirary">
              Без ответа
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};
